import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Alert } from '@vnetwork/ui/feedback';
import { Button } from '@vnetwork/ui/primitives';
import {
  currentWorkspaceRoute,
  organizationNameOf,
  tabNoticeAfterEvent,
  WORKING_PROJECT_KEY,
  sameId,
  useOrgCustomerId,
  useOrgWorkspaces,
  useSharedProjects,
  useWorkingProject,
} from '@/api/iam';
import { categoryByKey } from '@/lib/service-categories';
import { paths } from '@/lib/paths';
import { WORKSPACE_SERVICES } from '@/lib/workspace-services';
import {
  dismissNote,
  homeNoteFreePages,
  homeOrgNote,
  homeOrganizationName,
  isNoteDismissed,
  isServicePage,
  readNoteDismissals,
  serviceLandings,
  tabFollowTarget,
  workspaceLocation,
  workspaceDocumentTitle,
} from './home-org-note';
import styles from './WorkspaceHints.module.css';

/** The services whose workspaces the top bar loads; the same query keys, so reading them here costs no second fetch. */
const SWITCHABLE = WORKSPACE_SERVICES.filter((s) => s.switchable);
const LANDINGS = serviceLandings(WORKSPACE_SERVICES, (type) => categoryByKey(type)?.landingRoute);
/** Every page the home-organization note never shows on: the service landings and the pages about other organizations by design. */
const NOTE_FREE_PAGES = [...LANDINGS, ...homeNoteFreePages(paths.organizations.base)];

/** What the URL says about the workspace that is open: whose it is, which page, and whether it is a service page at all. */
function useOpenWorkspace() {
  const { pathname, search } = useLocation();
  const { workspaces } = useOrgWorkspaces(SWITCHABLE);
  const { data: shared = [] } = useSharedProjects();
  return useMemo(() => {
    const current = currentWorkspaceRoute(SWITCHABLE, workspaceLocation(pathname, search), workspaces);
    const organizationId = current?.match.organizationId;
    return {
      organizationId,
      page: current?.match.page ?? '',
      pageLabel: current?.service.pageLabel?.(current.match.page),
      organizationName: organizationId
        ? organizationNameOf(organizationId, shared, workspaces.find((w) => sameId(w.organizationId, organizationId))?.organizationName)
        : undefined,
      workspaces,
      // A workspace page or a service landing: Compute and Kubernetes pages never carry the home-organization note.
      onServicePage: isServicePage({ pathname, insideWorkspace: !!current, landings: NOTE_FREE_PAGES }),
    };
  }, [pathname, search, workspaces, shared]);
}

function session(): Storage | undefined {
  try {
    return globalThis.sessionStorage;
  } catch {
    return undefined; // reading the property itself throws when storage is blocked
  }
}

/** C9: closable, non-blocking note under the topbar on pages that always use the home organization. */
export function HomeOrgNote() {
  const { project, settled } = useWorkingProject();
  const home = useOrgCustomerId();
  const { data: shared = [] } = useSharedProjects();
  const { workspaces, onServicePage } = useOpenWorkspace();
  const [raw, setRaw] = useState<string | null>(() => readNoteDismissals(session()));
  const foreignId = project?.organizationId ?? '';
  const foreignName = organizationNameOf(foreignId, shared, workspaces.find((w) => sameId(w.organizationId, foreignId))?.organizationName);
  const text = homeOrgNote({
    stored: project,
    settled,
    home,
    homeName: homeOrganizationName({ projects: shared, workspaces, home }),
    foreignName,
    onWorkspaceRoute: onServicePage,
    dismissed: isNoteDismissed(raw, foreignId),
  });
  const dismiss = useCallback(() => {
    setRaw(dismissNote(session(), raw, foreignId));
  }, [raw, foreignId]);
  if (!text) return null;
  return (
    <Alert tone="info" role="status" className={styles.note} onDismiss={dismiss}>
      {text}
    </Alert>
  );
}

/** D2: `<page> · <Organization name>` as the document title while another organization's workspace is open. */
export function WorkspaceDocumentTitle() {
  const home = useOrgCustomerId();
  const { organizationId, page, pageLabel, organizationName } = useOpenWorkspace();
  const title = workspaceDocumentTitle({ home, workspaceOrganizationId: organizationId, page, pageLabel, organizationName });
  useEffect(() => {
    if (!title) return;
    const before = document.title;
    document.title = title;
    return () => {
      document.title = before;
    };
  }, [title]);
  return null;
}

/**
 * E6: the project was changed in another tab while this page is a workspace of a different organization; the page does
 * not move by itself. Reload goes to the new choice's workspace (`tabFollowTarget`): a plain reload would keep the foreign URL.
 */
export function TabChangedNotice() {
  const { organizationId, workspaces } = useOpenWorkspace();
  const { pathname, search } = useLocation();
  const [changed, setChanged] = useState(false);
  const open = useRef(organizationId);
  open.current = organizationId;
  useEffect(() => {
    const onStorage = (e: StorageEvent) =>
      setChanged((shown) => tabNoticeAfterEvent({ shown, key: e.key, newValue: e.newValue, openOrganizationId: open.current }));
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  // Another workspace is open now (the person moved, or followed): the old notice is about a page that is gone.
  useEffect(() => setChanged(false), [organizationId]);
  const reload = () => {
    const to = tabFollowTarget({
      registry: SWITCHABLE,
      loc: workspaceLocation(pathname, search),
      storedRaw: globalThis.localStorage?.getItem(WORKING_PROJECT_KEY) ?? null,
      workspaces,
    });
    if (to) window.location.assign(to);
    else window.location.reload();
  };
  if (!changed) return null;
  return (
    <Alert
      tone="info"
      role="status"
      className={styles.note}
      onDismiss={() => setChanged(false)}
      actions={
        <Button size="sm" variant="secondary" onClick={reload}>
          Reload
        </Button>
      }
    >
      Your project changed in another tab. Reload to follow.
    </Alert>
  );
}
